import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faWrench, 
  faSave,
  faHome,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../services/maintenanceService';
import { userService } from '../../services/userService';
import { tenantService } from '../../services/tenantService';
import { contractService } from '../../services/contractService';
import { propertyService } from '../../services/propertyService';

const CreateMaintenanceRequest = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    priority: 'medium',
    unitId: '',
    contactPhone: '',
    preferredTime: '',
    additionalNotes: ''
  });
  
  const [userProfile, setUserProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);

  const maintenanceTypes = [
    { value: 'plumbing', label: 'Plumbing Issues' },
    { value: 'electrical', label: 'Electrical Issues' },
    { value: 'hvac', label: 'HVAC/Heating & Cooling' },
    { value: 'appliance', label: 'Appliance Repair' },
    { value: 'structural', label: 'Structural Issues' },
    { value: 'pest_control', label: 'Pest Control' },
    { value: 'cleaning', label: 'Cleaning Services' },
    { value: 'landscaping', label: 'Landscaping' },
    { value: 'security', label: 'Security Issues' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'high', label: 'High', color: 'danger' },
    { value: 'urgent', label: 'Urgent', color: 'danger' }
  ];

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      // Get user profile
      const profile = await userService.getUserProfile();
      setUserProfile(profile);
      
      // Get tenant document using the authUid or email
      const tenants = await tenantService.getTenantsByCompany(profile.companyId);
      let tenantData = tenants.find(t => t.authUid === profile.uid);
      
      // If not found by authUid, try to find by email
      if (!tenantData) {
        console.log('Tenant not found by authUid, looking by email:', profile.email);
        tenantData = tenants.find(t => t.email === profile.email);
      }
      
      if (tenantData) {
        setTenant(tenantData);
        // Pre-fill form with tenant data
        setFormData(prev => ({
          ...prev,
          contactPhone: tenantData.phone || '',
          unitId: tenantData.unitId || ''
        }));
        
        // Fetch units after tenant data is loaded
        await fetchUnits(tenantData.id, profile.companyId);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user data');
    }
  };

  const fetchUnits = async (tenantId, companyId) => {
    try {
      setLoadingUnits(true);
      console.log('Fetching units for tenant:', tenantId, 'company:', companyId);
      
      // Get active contracts for the tenant
      const contracts = await contractService.getContractsByTenant(tenantId);
      console.log('Found contracts:', contracts.length);
      
      // Filter for active contracts
      const activeContracts = contracts.filter(contract => 
        contract.status === 'active' || contract.status === 'pending'
      );
      console.log('Active contracts:', activeContracts.length);
      
      if (activeContracts.length === 0) {
        setUnitOptions([]);
        return;
      }
      
      // Get property units for each contract
      const unitPromises = activeContracts.map(async (contract) => {
        try {
          const unit = await propertyService.getPropertyUnitById(contract.propertyUnitId);
          const group = unit.groupId ? await propertyService.getPropertyGroupById(unit.groupId) : null;
          
          return {
            id: unit.id,
            label: `${unit.unitNumber} - ${group ? group.name : 'Unknown Property'}`,
            unitNumber: unit.unitNumber,
            groupName: group ? group.name : 'Unknown Property',
            contractId: contract.id
          };
        } catch (error) {
          console.error('Error fetching unit for contract:', contract.id, error);
          return null;
        }
      });
      
      const units = (await Promise.all(unitPromises)).filter(unit => unit !== null);
      console.log('Processed units:', units);
      
      setUnitOptions(units);
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Failed to load property units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.type) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const requestData = {
        ...formData,
        tenantId: tenant.id,
        tenantName: tenant.name,
        tenantEmail: tenant.email,
        companyId: userProfile.companyId,
        unitId: formData.unitId || tenant.unitId,
        contactPhone: formData.contactPhone || tenant.phone
      };

      await maintenanceService.createMaintenanceRequest(requestData);
      
      setSuccess('Maintenance request created successfully!');
      setTimeout(() => {
        navigate('/tenant/maintenance');
      }, 2000);
    } catch (error) {
      console.error('Error creating maintenance request:', error);
      setError('Failed to create maintenance request: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            as={Link} 
            to="/tenant/maintenance" 
            variant="outline-secondary" 
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Maintenance
          </Button>
          <div>
            <h2 className="mb-1">Create Maintenance Request</h2>
            <p className="text-muted mb-0">Submit a new maintenance request</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to="/tenant/dashboard" variant="outline-secondary">
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faWrench} className="text-primary me-2" />
                Maintenance Request Form
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Request Title *</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Brief description of the issue"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Maintenance Type *</Form.Label>
                      <Form.Select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select maintenance type</option>
                        {maintenanceTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Priority Level</Form.Label>
                      <Form.Select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                      >
                        {priorityOptions.map(priority => (
                          <option key={priority.value} value={priority.value}>
                            {priority.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Number *</Form.Label>
                      {loadingUnits ? (
                        <div className="d-flex align-items-center">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading units...
                        </div>
                      ) : (
                        <>
                          <Form.Select
                            name="unitId"
                            value={formData.unitId}
                            onChange={handleInputChange}
                            required
                            disabled={unitOptions.length === 0}
                          >
                            <option value="">{unitOptions.length === 0 ? 'No units available' : 'Select your unit'}</option>
                            {unitOptions.map(unit => (
                              <option key={unit.id} value={unit.id}>
                                {unit.label}
                              </option>
                            ))}
                          </Form.Select>
                          {unitOptions.length === 0 && (
                            <div className="text-danger mt-2">
                              No active property units found. Please contact your property manager.<br/>
                              <span style={{fontSize: '0.9em', color: '#888'}}>If you believe this is an error, please refresh or try again later.</span>
                            </div>
                          )}
                        </>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Detailed Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please provide a detailed description of the issue..."
                    required
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="contactPhone"
                        value={formData.contactPhone}
                        onChange={handleInputChange}
                        placeholder="Phone number for contact"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Preferred Time</Form.Label>
                      <Form.Select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                      >
                        <option value="">Any time</option>
                        <option value="morning">Morning (8 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                        <option value="evening">Evening (4 PM - 8 PM)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Additional Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    placeholder="Any additional information or special instructions..."
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {loading ? 'Creating...' : 'Submit Request'}
                  </Button>
                  <Button 
                    as={Link} 
                    to="/tenant/maintenance" 
                    variant="outline-secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreateMaintenanceRequest; 